import { For, Show, createSignal, onCleanup, onMount } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import type { PacketRecordingDto, VektorMonitorBotDto } from '../api';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { formatDateTime } from '../services/dateFormat';
import { firstError } from '../api/envelope';
import { formatBytes } from '../services/numberFormat';

function remaining(captureUntilUtc: string, now: number): string {
    let seconds = Math.max(0, Math.floor((new Date(captureUntilUtc).getTime() - now) / 1000));
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export default function AdminPacketRecordings() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [recordings, setRecordings] = createStore<PacketRecordingDto[]>([]);
    const [bots, setBots] = createSignal<VektorMonitorBotDto[]>([]);
    const [selectedBot, setSelectedBot] = createSignal('');
    const [hours, setHours] = createSignal(24);
    // 0 means the whole capture. Page-level rather than per row: the filename records which range was
    // taken, so there is no ambiguity afterwards about what a given file contains.
    const [rangeMinutes, setRangeMinutes] = createSignal(0);
    const [loading, setLoading] = createSignal(true);
    const [busy, setBusy] = createSignal<string | null>(null);
    const [now, setNow] = createSignal(Date.now());

    async function fetchRecordings() {
        try {
            const res = await api.packetRecordingAdmin.list();
            if (isOk(res.data)) {
                setRecordings(reconcile(res.data.value.recordings ?? [], { key: 'recordingId' }));
            }
        } catch { /* the poll retries; a toast on every tick would be noise */ }
        setLoading(false);
    }

    async function fetchBots() {
        try {
            const res = await api.packetRecordingAdmin.listRecordableBots();
            if (isOk(res.data)) setBots(res.data.value.bots ?? []);
        } catch { /* the start dropdown stays empty and the button stays disabled */ }
    }

    async function startRecording() {
        const botId = selectedBot();
        if (!botId) return;
        setBusy(botId);
        try {
            const res = await api.packetRecordingAdmin.start(botId, { hours: hours() });
            if (!isOk(res.data)) {
                const failure = firstError(res.data);
                toast?.error(
                    failure === 'storage_unavailable' ? t('PACKET_RECORDING_STORAGE_UNAVAILABLE')
                        : failure === 'already_recording' ? t('PACKET_RECORDING_ALREADY_RUNNING')
                            : t('PACKET_RECORDING_START_FAILED'));
                return;
            }
            const name = bots().find(b => b.botId === botId)?.username ?? botId;
            toast?.success(t('PACKET_RECORDING_STARTED_TOAST', name));
            setSelectedBot('');
            await fetchRecordings();
        } catch {
            toast?.error(t('PACKET_RECORDING_START_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    async function stopRecording(recording: PacketRecordingDto) {
        // Explicit label: a danger confirm otherwise defaults to "Delete", and stopping keeps the data.
        const confirmed = await showConfirm(
            t('PACKET_RECORDING_STOP_CONFIRM', recording.botUsername ?? ''),
            true,
            { confirmLabel: t('PACKET_RECORDING_STOP') });
        if (!confirmed) return;
        setBusy(recording.recordingId!);
        try {
            const res = await api.packetRecordingAdmin.stop(recording.recordingId!);
            if (!isOk(res.data)) {
                toast?.error(t('PACKET_RECORDING_STOP_FAILED'));
                return;
            }
            toast?.info(t('PACKET_RECORDING_STOPPED_TOAST', recording.botUsername ?? ''));
            await fetchRecordings();
        } catch {
            toast?.error(t('PACKET_RECORDING_STOP_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    async function deleteRecording(recording: PacketRecordingDto) {
        const confirmed = await showConfirm(
            t('PACKET_RECORDING_DELETE_CONFIRM', recording.botUsername ?? '', formatBytes(recording.totalCompressedBytes ?? 0)),
            true);
        if (!confirmed) return;
        setBusy(recording.recordingId!);
        try {
            const res = await api.packetRecordingAdmin.delete(recording.recordingId!);
            if (!isOk(res.data)) {
                toast?.error(t('PACKET_RECORDING_DELETE_FAILED'));
                return;
            }
            toast?.info(t('PACKET_RECORDING_DELETED_TOAST', recording.botUsername ?? ''));
            await fetchRecordings();
        } catch {
            toast?.error(t('PACKET_RECORDING_DELETE_FAILED'));
        } finally {
            setBusy(null);
        }
    }

    // Plain navigation rather than the fetch-then-blob helper: a 72h capture is far too large to hold
    // in memory, and a navigation lets the browser stream it straight to disk under the session cookie.
    function downloadUrl(recording: PacketRecordingDto): string {
        const minutes = rangeMinutes();
        const range = minutes > 0 ? `?lastMinutes=${minutes}` : '';
        return `/api/vektor/admin/packet-recordings/${recording.recordingId}/download${range}`;
    }

    // The started-at stamp keeps repeated captures of one bot from colliding in the downloads folder,
    // and the range keeps a windowed pull from overwriting the full one.
    function downloadName(recording: PacketRecordingDto): string {
        const startedAt = (recording.startedAtUtc ?? '').replace(/[:.]/g, '-').replace(/T/, '_').slice(0, 19);
        const minutes = rangeMinutes();
        const range = minutes > 0 ? `-last${minutes}m` : '';
        return `packet-recording-${recording.botUsername}-${startedAt}${range}.jsonl.gz`;
    }

    onMount(() => {
        void fetchBots();
        void fetchRecordings();
        const refresh = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchRecordings();
        }, 3000);
        const ticker = window.setInterval(() => {
            if (document.visibilityState === 'visible') setNow(Date.now());
        }, 1000);
        onCleanup(() => { clearInterval(refresh); clearInterval(ticker); });
    });

    const recordingBotIds = () => new Set(recordings.filter(r => r.active).map(r => r.botId));

    return (
        <div class="v" style={{ 'max-width': '1100px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <h1 style={{ margin: 0 }}><Text val="PACKET_RECORDING_TITLE" /></h1>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                    <Text val="PACKET_RECORDING_SUBTITLE" />
                </span>
            </div>

            <div class="fcard h" style={{ 'align-items': 'flex-end', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <label class="v" style={{ gap: 'var(--spacing-xs)', flex: '1', 'min-width': '14rem' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="PACKET_RECORDING_PICK_BOT" />
                    </span>
                    <select value={selectedBot()} onInput={(e) => setSelectedBot(e.currentTarget.value)}>
                        <option value="">--</option>
                        <For each={bots().filter(b => !recordingBotIds().has(b.botId))}>{(bot) =>
                            <option value={bot.botId}>{bot.username}</option>
                        }</For>
                    </select>
                </label>
                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="PACKET_RECORDING_DURATION" />
                    </span>
                    <select value={hours()} onInput={(e) => setHours(parseInt(e.currentTarget.value, 10))}>
                        <option value="24">24h</option>
                        <option value="48">48h</option>
                        <option value="72">72h</option>
                    </select>
                </label>
                <button class="btn btn-primary" disabled={!selectedBot() || busy() === selectedBot()} onClick={() => void startRecording()}>
                    <i class="fa-solid fa-circle-dot" /> <Text val="PACKET_RECORDING_START" />
                </button>
                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="PACKET_RECORDING_RANGE" />
                    </span>
                    <select value={rangeMinutes()} onInput={(e) => setRangeMinutes(parseInt(e.currentTarget.value, 10))}>
                        <option value="0">{t('PACKET_RECORDING_RANGE_ALL')}</option>
                        <option value="15">{t('PACKET_RECORDING_RANGE_MINUTES', '15')}</option>
                        <option value="60">{t('PACKET_RECORDING_RANGE_HOURS', '1')}</option>
                        <option value="360">{t('PACKET_RECORDING_RANGE_HOURS', '6')}</option>
                        <option value="1440">{t('PACKET_RECORDING_RANGE_HOURS', '24')}</option>
                    </select>
                </label>
            </div>

            <Show when={!loading()} fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonAuditRow /><SkeletonAuditRow /><SkeletonAuditRow /></div>}>
                <Show when={recordings.length > 0} fallback={<EmptyState icon="fa-file-waveform" title={t('PACKET_RECORDING_EMPTY')} />}>
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <For each={recordings}>{(recording) => {
                            return (
                                <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-lg)' }}>
                                    <i
                                        class={recording.active ? 'fa-solid fa-circle-dot' : 'fa-solid fa-file-waveform'}
                                        style={{
                                            color: recording.active ? 'var(--success-500)' : 'var(--dark-text-muted)',
                                            'font-size': 'var(--font-size-lg)',
                                        }}
                                    />
                                    <div class="v" style={{ flex: '1', gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                        <div style={{ 'font-weight': 'var(--font-weight-semibold)' }}>{recording.botUsername}</div>
                                        <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                            <Text val="PACKET_RECORDING_STARTED" />: {formatDateTime(recording.startedAtUtc!)}
                                            {' · '}
                                            <Show
                                                when={recording.active}
                                                fallback={<><Text val="PACKET_RECORDING_STOPPED" />: {t(`PACKET_RECORDING_REASON_${(recording.stopReason ?? '').toUpperCase()}`)}</>}
                                            >
                                                <Text val="PACKET_RECORDING_ENDS_IN" />: {remaining(recording.captureUntilUtc!, now())}
                                            </Show>
                                            {' · '}{formatBytes(recording.totalCompressedBytes ?? 0)}
                                            {' · '}{recording.totalRecords ?? 0} <Text val="PACKET_RECORDING_RECORDS" />
                                            <Show when={recording.active && !recording.attached}>
                                                {' · '}<Text val="PACKET_RECORDING_BOT_OFFLINE" />
                                            </Show>
                                        </div>
                                        <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                                            <Text val="PACKET_RECORDING_STARTED_BY" />: {recording.startedByUsername}
                                        </div>
                                    </div>
                                    <Show when={recording.active}>
                                        <button
                                            class="btn btn-danger btn-sm"
                                            disabled={busy() === recording.recordingId}
                                            onClick={() => void stopRecording(recording)}
                                        >
                                            <i class="fa-solid fa-stop" /> <Text val="PACKET_RECORDING_STOP" />
                                        </button>
                                    </Show>
                                    {/* download and rel=external both stop the router swallowing the
                                        click into a client-side route; without them it never reaches
                                        the network. Keep both if the router's rules ever change. */}
                                    <a
                                        class="btn btn-secondary btn-sm"
                                        href={downloadUrl(recording)}
                                        download={downloadName(recording)}
                                        rel="external"
                                    >
                                        <i class="fa-solid fa-download" /> <Text val="PACKET_RECORDING_DOWNLOAD" />
                                    </a>
                                    <button
                                        class="btn btn-danger btn-sm"
                                        disabled={busy() === recording.recordingId}
                                        onClick={() => void deleteRecording(recording)}
                                    >
                                        <i class="fa-solid fa-trash" /> <Text val="PACKET_RECORDING_DELETE" />
                                    </button>
                                </div>
                            );
                        }}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
